<script setup lang="ts">
import BaseLayout from "@/layouts/BaseLayout.vue";
import {computed, ref} from "vue";
import SearchField from "@/components/forms/SearchField.vue";
import {DataTableHeader} from "vuetify/framework";
import SortField from "@/components/forms/SortField.vue";
import {CapacityFramework, CapacityFrameworkSummary, Pagination, PaginationFilters} from "@/types";
import {router} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import CapacityFrameworkLayout from "@/layouts/CapacityFrameworkLayout.vue";
import VersionChip from "@/components/chips/VersionChip.vue";
import TitleAvatar from "@/components/avatars/TitleAvatar.vue";
import CapacityFrameworkQuickActionsBtn
    from "@/Pages/capacity_frameworks/components/CapacityFrameworkQuickActionsBtn.vue";
import {useCapacityFramework} from "@/composables/capacity_framework";
import {useTeam} from "@/composables/team";

defineOptions({layout: [BaseLayout, CapacityFrameworkLayout]})

const {isGuest, canCreateAssessment} = useTeam()
const {isAdmin} = useCapacityFramework()

interface Props {
    title: string
    capacityFramework: CapacityFramework
    category: 'overview'|'users'|'assessments'|'progression'|'public'|'versions'
    items: Pagination<CapacityFrameworkSummary>
    filters: PaginationFilters
}

const props = defineProps<Props>();

const search = ref<string | null>(props.filters.search)
const sortBy = ref<string>(props.filters.sort_by)
const sortDir = ref<'asc' | 'desc'>(props.filters.sort_dir)
const page = ref<number>(props.filters.page)

const lastPage = computed(() => {
    return props.items.last_page || 1;
});

const sortItems: Array<{ title: string; value: string }> = [
    {
        title: 'Name',
        value: 'name'
    },
    {
        title: 'Created date',
        value: 'created_at'
    },
    {
        title: 'Updated date',
        value: 'updated_at'
    },
    {
        title: 'Version',
        value: 'version_id'
    }
]

const headers: Array<DataTableHeader> = [
    {
        title: 'Name',
        value: 'name',
        sortable: false,
        align: 'start',
        width: '95%'
    },
    {
        title: 'Actions',
        value: 'actions',
        sortable: false,
        align: 'end',
        width: '5%'
    },
]

const loadItems = () => {
    const params: Record<string, string | null> = {
        search: search.value,
        sort_by: sortBy.value,
        sort_dir: sortDir.value,
        page: page.value.toString()
    };

    // Remove empty parameters
    Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
            delete params[key];
        }
    });

    const uri = route('capacity-frameworks.versions.index', {capacityFramework: props.capacityFramework.id});

    router.get(
        uri,
        params,
        {
            preserveState: true,
        }
    );
}

const goToCapacityFramework = (id: number, version: number) => {
    router.get(route('capacity-frameworks.show', {capacityFramework: id, version: version}));
};

const editCapacityFramework = (id: number, version: number) => {
    router.get(route('capacity-frameworks.edit', {capacityFramework: id, version: version}));
};

const deleteCapacityFramework = (id: number, version: number) => {
    router.delete(route('capacity-frameworks.destroy', {capacityFramework: id, version: version}));
};

const createNewAssessment = (id: number, version: number) => {
    router.get(route('assessments.create'), {
        capacity_framework_id: id,
        capacity_framework_version_id: version
    });
}
</script>

<template>
    <v-row class="mt-3 bg-grey-lighten-5 border-t-sm border-b-sm">
        <v-col
            cols="12"
            md="9"
            class="py-4"
        >
            <search-field
                v-model="search"
                @click="loadItems"
            />
        </v-col>
        <v-col
            cols="12"
            md="3"
            class="py-4"
        >
            <sort-field
                v-model:sort-by="sortBy"
                v-model:sort-dir="sortDir"
                :items="sortItems"
                @update:sort-by="loadItems"
                @update:sort-dir="loadItems"
            />
        </v-col>
    </v-row>
    <v-row>
        <v-col
            cols="12"
            class="py-0"
        >
            <v-data-table
                :headers="headers"
                :items="props.items.data"
                :items-per-page="props.items.per_page"
                :page="props.items.current_page"
                disable-sort
                hide-default-header
                hide-default-footer
                no-data-text="No results found."
            >
                <template #no-data>
                    <v-sheet class="mt-10">
                        <v-row
                            class="fill-height"
                            align="center"
                            justify="center"
                        >
                            <v-col
                                cols="12"
                                class="text-center"
                            >
                                <v-avatar size="128">
                                    <v-icon
                                        size="128"
                                        color="grey-lighten-2"
                                    >
                                        mdi-emoticon-sad-outline
                                    </v-icon>
                                </v-avatar>
                                <h2 class="text-h5 font-weight-bold">
                                    No capacity frameworks found.
                                </h2>
                                <p>
                                    Try adjusting your search or category criteria.
                                </p>
                            </v-col>
                        </v-row>
                    </v-sheet>
                </template>
                <template #[`item.name`]="{ item }">
                    <v-list-item>
                        <template #prepend>
                            <title-avatar
                                :id="item.id"
                                :title="item.name"
                                @click="goToCapacityFramework(item.id, item.version_id)"
                            />
                        </template>
                        <v-list-item-title
                            class="text-body-2 font-weight-bold"
                        >
                            <span
                                class="custom-link"
                                @click="goToCapacityFramework(item.id, item.version_id)"
                            >
                                {{ item.name }}
                            </span>
                            <v-chip
                                variant="flat"
                                :color="item.status === 'published' ? '#B8E2C2' : '#C0DAF8'"
                                size="x-small"
                                class="ml-2 px-2 text-caption float-end"
                                :class="item.status === 'published' ? 'text-green-darken-4' : 'text-blue-darken-4'"
                            >
                                {{ item.status === 'published' ? 'Published' : 'Draft' }}
                            </v-chip>
                            <version-chip>
                                {{ item.version_id }}
                            </version-chip>
                        </v-list-item-title>
                    </v-list-item>
                </template>
                <template #[`item.actions`]="{ item }">
                    <capacity-framework-quick-actions-btn
                        :disabled="isGuest"
                        :capacity-framework="item"
                        :hide-assess-action="item.status === 'draft' || !canCreateAssessment"
                        :hide-admin-actions="!isAdmin"
                        @submit="createNewAssessment(item.id, item.version_id)"
                        @update="editCapacityFramework(item.id, item.version_id)"
                        @delete="deleteCapacityFramework(item.id, item.version_id)"
                    />
                </template>
            </v-data-table>
            <v-row
                v-if="items.total > 0"
                class="justify-center mt-5"
            >
                <v-pagination
                    v-model="page"
                    :length="lastPage"
                    density="compact"
                    active-color="primary"
                    @update:model-value="loadItems"
                />
            </v-row>
        </v-col>
    </v-row>
</template>

<style scoped>
:deep(.v-list-item__prepend) {
    width: 42px;
}

:deep(.text-sm) {
    font-size: 0.75rem !important;
}
</style>
